import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { LayoutDashboard, FileText, Navigation, Video, Box, Grid as GridIcon, Mail, Type, Settings, User, Star, Users } from 'lucide-react';
import { ModeToggle } from '@/components/mode-toggle';
import ProfileModal from './ProfileModal';

const Sidebar = () => {
    const location = useLocation();
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [currentUser, setCurrentUser] = useState(() => JSON.parse(localStorage.getItem('userInfo')));

    const links = [
        { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
        { name: 'Branding', path: '/admin/branding', icon: Star },
        { name: 'Hero Section', path: '/admin/hero', icon: Video },
        { name: 'Highlights', path: '/admin/highlights', icon: FileText },
        { name: '3D Models', path: '/admin/models', icon: Box },
        { name: 'Meet Our Team', path: '/admin/team', icon: Users }, // Added Team link
        { name: 'Navigation', path: '/admin/nav', icon: Navigation },
        { name: 'Bento Grid', path: '/admin/bento', icon: GridIcon },
        { name: 'Contact Us', path: '/admin/contact', icon: Mail },
        { name: 'Footer', path: '/admin/footer', icon: Type },
    ];

    return (
        <>
            <div className="flex flex-col w-72 h-screen px-4 py-6 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 transition-colors duration-300">
                <div
                    onClick={() => setIsProfileOpen(true)}
                    className="flex items-center p-3 mb-8 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors group"
                >
                    <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold mr-3">
                        {currentUser?.username?.charAt(0).toUpperCase() || 'A'}
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors">SNC Admin</h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Edit Profile</p>
                    </div>
                </div>

                <nav className="flex flex-col space-y-2 flex-1 overflow-y-auto">
                    {links.map((link) => {
                        const Icon = link.icon;
                        const isActive = location.pathname === link.path;
                        return (
                            <Link
                                key={link.path}
                                to={link.path}
                                className={cn(
                                    "flex items-center px-4 py-3 rounded-xl transition-all duration-200 font-medium",
                                    isActive
                                        ? "bg-black text-white dark:bg-white dark:text-black shadow-lg shadow-black/10 dark:shadow-white/5"
                                        : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white"
                                )}
                            >
                                <Icon className="w-5 h-5 mr-3" />
                                {link.name}
                            </Link>
                        );
                    })}
                </nav>

                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 space-y-2">
                    <Link
                        to="/admin/settings"
                        className={cn(
                            "flex items-center px-4 py-3 rounded-xl transition-all duration-200 font-medium",
                            location.pathname === '/admin/settings'
                                ? "bg-black text-white dark:bg-white dark:text-black"
                                : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                        )}
                    >
                        <Settings className="w-5 h-5 mr-3" />
                        Settings
                    </Link>

                    <div className="flex items-center justify-between px-2 py-2">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400 ml-2">Theme</span>
                        <ModeToggle />
                    </div>

                    <button
                        onClick={() => {
                            localStorage.removeItem('userInfo');
                            window.location.href = '/login';
                        }}
                        className="flex items-center w-full px-4 py-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-xl transition-colors font-medium"
                    >
                        <User className="w-5 h-5 mr-3" />
                        Logout
                    </button>
                </div>
            </div>

            <ProfileModal
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                user={currentUser}
                onUpdate={(updated) => setCurrentUser(updated)}
            />
        </>
    );
};

export default Sidebar;
